import {
  Alert,
  Box,
  Button,
  CircularProgress,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import PageLayout from "../../components/common/page-layout";
import { useCrownEnv } from "../../components/crown-data-import/env/env-context";
import {
  useSaveTenediPartnershipMutation,
  useTenediConnectionQuery,
  useUpdateTenediCarrierIdentityMutation,
  useUpdateTenediCustomerIdentityMutation,
  type TenediPartnershipFieldsFragment,
} from "../../generated/graphql";
import { ROUTES } from "../../route-paths";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import CarrierIdentityCard from "./components/carrier-identity-card";
import ChangeSummary, {
  type ChangeSummaryValue,
} from "./components/change-summary";
import ConnectionPickers, {
  type CustomerOption,
} from "./components/connection-pickers";
import CustomerIdentityCard from "./components/customer-identity-card";
import EnvironmentBanner from "./components/environment-banner";
import PartnershipDialog, {
  type PartnershipSave,
} from "./components/partnership-dialog";
import PartnershipsTable from "./components/partnerships-table";
import ReadinessPanel from "./components/readiness-panel";
import {
  type CarrierFormValues,
  confirmProductionFor,
  type CustomerFormValues,
  isProductionTarget,
  isSaveAllowed,
  toCarrierInput,
  toCustomerInput,
  toSavePartnershipInput,
} from "./tenedi-connection-forms";
import { PRODUCTION_SAVE_HINT } from "./tenedi-connection-copy";
import { CARRIER_PARAM, CUSTOMER_PARAM } from "./tenedi-connection-links";
import { INITIAL_CARD_REVISIONS, nextCardRevisions } from "./card-revisions";

const styles = {
  body: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pt: 2,
  } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    p: 6,
  } satisfies SxProps<Theme>,
};

const TITLE = "Tenedi connections";

type ConnectionIds = { companyUuid: string; organizationUuid: string };

const isSameConnection = (
  a: ConnectionIds | null,
  b: ConnectionIds | null,
): boolean =>
  a !== null &&
  b !== null &&
  a.companyUuid === b.companyUuid &&
  a.organizationUuid === b.organizationUuid;

/**
 * The dialog is closed, adding a partnership (null), or editing one. It keeps
 * the connection it opened on, so Back or Forward to another connection hides
 * it rather than saving it against the wrong customer.
 */
type DialogState =
  | { open: false }
  | {
      open: true;
      ids: ConnectionIds;
      original: TenediPartnershipFieldsFragment | null;
    };

const CLOSED: DialogState = { open: false };

/**
 * Staff page that sets up one carrier-to-customer EDI connection through
 * Tenedi (TMS-2981): both sides' EDI identity, the partnerships, and the
 * readiness checklist, with a link into the outbound test bench.
 *
 * The URL carries the carrier and customer uuids, so a link opens the same
 * connection. The backend owns every rule and the production opt-in; the page
 * adds the typed `prod` confirmation in front of that opt-in.
 */
const TenediConnectionsPage = () => {
  const { canAccess, loading: accessLoading } = useIsInternalAdmin();
  const { env } = useCrownEnv();
  const [searchParams, setSearchParams] = useSearchParams();
  const carrierUuid = searchParams.get(CARRIER_PARAM);
  const customerUuid = searchParams.get(CUSTOMER_PARAM);

  const [confirmation, setConfirmation] = useState("");
  const [summary, setSummary] = useState<ChangeSummaryValue | null>(null);
  const [dialog, setDialog] = useState<DialogState>(CLOSED);
  const [revisions, setRevisions] = useState(INITIAL_CARD_REVISIONS);

  const ids = useMemo<ConnectionIds | null>(
    () =>
      carrierUuid === null || customerUuid === null
        ? null
        : { companyUuid: carrierUuid, organizationUuid: customerUuid },
    [carrierUuid, customerUuid],
  );
  const currentIds = useRef(ids);
  useEffect(() => {
    currentIds.current = ids;
  }, [ids]);

  const { data, loading, error, refetch } = useTenediConnectionQuery({
    errorPolicy: "none",
    skip: !canAccess || ids === null,
    variables: {
      input: ids ?? { companyUuid: "", organizationUuid: "" },
    },
  });
  const connection = data?.tenediConnection ?? null;

  const [updateCarrier] = useUpdateTenediCarrierIdentityMutation({
    errorPolicy: "none",
  });
  const [updateCustomer] = useUpdateTenediCustomerIdentityMutation({
    errorPolicy: "none",
  });
  const [savePartnership] = useSaveTenediPartnershipMutation({
    errorPolicy: "none",
  });

  const production = isProductionTarget(env, connection?.production);
  const saveAllowed = isSaveAllowed(production, confirmation);
  const confirmProduction = confirmProductionFor(production, confirmation);

  const selectedCustomer = useMemo<CustomerOption | null>(
    () =>
      connection === null
        ? null
        : {
            uuid: connection.customer.uuid,
            displayName: connection.customer.displayName,
          },
    [connection],
  );

  const handleCarrierChange = useCallback(
    (uuid: string | null) => {
      setSummary(null);
      setSearchParams(uuid === null ? {} : { [CARRIER_PARAM]: uuid });
    },
    [setSearchParams],
  );
  const handleCustomerChange = useCallback(
    (uuid: string | null) => {
      setSummary(null);
      if (carrierUuid === null) {
        return;
      }
      setSearchParams(
        uuid === null
          ? { [CARRIER_PARAM]: carrierUuid }
          : { [CARRIER_PARAM]: carrierUuid, [CUSTOMER_PARAM]: uuid },
      );
    },
    [carrierUuid, setSearchParams],
  );

  /**
   * Shows a save's summary and remounts its card, but only while the operator
   * still has the connection the save started on. A save that lands after a
   * switch would otherwise remount the new connection's card, dropping its
   * unsaved edits, and show the old connection's changes on it.
   */
  const afterSave = useCallback(
    async (savedIds: ConnectionIds, next: ChangeSummaryValue) => {
      if (!isSameConnection(savedIds, currentIds.current)) {
        return;
      }
      setSummary(next);
      await refetch();
      if (isSameConnection(savedIds, currentIds.current)) {
        setRevisions((current) => nextCardRevisions(current, next.scope));
      }
    },
    [refetch],
  );

  const handleSaveCarrier = useCallback(
    async (values: CarrierFormValues) => {
      if (ids === null) {
        return;
      }
      const result = await updateCarrier({
        variables: {
          input: toCarrierInput(ids.companyUuid, values, confirmProduction),
        },
      });
      await afterSave(ids, {
        title: "Carrier IDs saved",
        scope: "carrier",
        changes: result.data?.updateTenediCarrierIdentity.changes ?? [],
      });
    },
    [afterSave, confirmProduction, ids, updateCarrier],
  );

  const handleSaveCustomer = useCallback(
    async (values: CustomerFormValues) => {
      if (ids === null) {
        return;
      }
      const result = await updateCustomer({
        variables: { input: toCustomerInput(ids, values, confirmProduction) },
      });
      await afterSave(ids, {
        title: "Customer IDs saved",
        scope: "customer",
        changes: result.data?.updateTenediCustomerIdentity.changes ?? [],
      });
    },
    [afterSave, confirmProduction, ids, updateCustomer],
  );

  const handleSavePartnership = useCallback(
    async ({ values, confirmCutover }: PartnershipSave) => {
      if (!dialog.open) {
        return;
      }
      const opened = dialog;
      const result = await savePartnership({
        variables: {
          input: toSavePartnershipInput({
            ids: opened.ids,
            partnershipUuid: opened.original?.uuid ?? null,
            values,
            confirmCutover,
            confirmProduction,
          }),
        },
      });
      const saved = result.data?.saveTenediPartnership;
      setDialog((current) => (current === opened ? CLOSED : current));
      await afterSave(opened.ids, {
        title:
          saved?.created === true
            ? `Partnership created on ${saved.partnership.companyStandardCarrierAlphaCode ?? ""}`
            : "Partnership saved",
        scope: "partnership",
        changes: saved?.changes ?? [],
      });
    },
    [afterSave, confirmProduction, dialog, savePartnership],
  );

  const handleAdd = useCallback(() => {
    if (ids !== null) {
      setDialog({ open: true, ids, original: null });
    }
  }, [ids]);
  const handleEdit = useCallback(
    (original: TenediPartnershipFieldsFragment) => {
      if (ids !== null) {
        setDialog({ open: true, ids, original });
      }
    },
    [ids],
  );
  const handleCloseDialog = useCallback(() => {
    setDialog(CLOSED);
  }, []);
  const handleCloseSummary = useCallback(() => {
    setSummary(null);
  }, []);

  const dialogOnThisConnection =
    dialog.open && isSameConnection(dialog.ids, ids);

  if (accessLoading) {
    return (
      <PageLayout title={TITLE}>
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout title={TITLE}>
        <Box sx={styles.body}>
          <Alert severity="error">
            You do not have access to internal tools.
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      actions={
        <Button component={RouterLink} to={ROUTES.OUTBOUND_TEST_BENCH}>
          Outbound test bench
        </Button>
      }
      subtitle="Set up a carrier and customer for EDI through Tenedi."
      title={TITLE}
    >
      <Box sx={styles.body}>
        <EnvironmentBanner
          confirmation={confirmation}
          env={env}
          production={production}
          serverEnvironment={connection?.environment ?? null}
          onConfirmationChange={setConfirmation}
        />
        <ConnectionPickers
          carrierUuid={carrierUuid}
          customer={selectedCustomer}
          onCarrierChange={handleCarrierChange}
          onCustomerChange={handleCustomerChange}
        />
        {loading && (
          <Box sx={styles.centered}>
            <CircularProgress />
          </Box>
        )}
        {error !== undefined && (
          <Alert severity="error">
            Could not load this connection: {error.message}
          </Alert>
        )}
        {summary !== null && (
          <ChangeSummary summary={summary} onClose={handleCloseSummary} />
        )}
        {connection !== null && (
          <>
            <ReadinessPanel
              items={connection.readiness}
              ready={connection.ready}
            />
            <CarrierIdentityCard
              key={`carrier-${connection.carrier.uuid}-${revisions.carrier}`}
              carrier={connection.carrier}
              saveDisabled={!saveAllowed}
              onSave={handleSaveCarrier}
            />
            <CustomerIdentityCard
              key={`customer-${connection.customer.uuid}-${revisions.customer}`}
              customer={connection.customer}
              saveDisabled={!saveAllowed}
              onSave={handleSaveCustomer}
            />
            <PartnershipsTable
              addDisabled={
                connection.carrier.scacs.length === 0 || !saveAllowed
              }
              addHint={saveAllowed ? null : PRODUCTION_SAVE_HINT}
              partnerships={connection.partnerships}
              onAdd={handleAdd}
              onEdit={handleEdit}
            />
            {connection.carrier.scacs.length === 0 && (
              <Alert severity="info">
                Give the carrier a SCAC under Carrier identity before adding a
                partnership.
              </Alert>
            )}
          </>
        )}
        {dialog.open && dialogOnThisConnection && connection !== null && (
          <PartnershipDialog
            carrierScacs={connection.carrier.scacs}
            original={dialog.original}
            saveDisabled={!saveAllowed}
            onClose={handleCloseDialog}
            onSave={handleSavePartnership}
          />
        )}
      </Box>
    </PageLayout>
  );
};

export default TenediConnectionsPage;
